<template>
  <div>
    <form class="md:flex md:align-top" @submit.prevent="handleSubmitFilters">
      <div class="grow">
        <div class="mb-16 grid flex-1 grid-cols-2 gap-16 lg:grid-cols-5">
          <RevInputText
            v-if="shouldShowFilter(FILTER_FIELDS.TITLE)"
            :id="FILTER_FIELDS.TITLE"
            v-model="filtersValues.title"
            :label="i18n(sharedTranslations.title)"
            type="text"
          />
          <RevInputText
            v-if="shouldShowFilter(FILTER_FIELDS.SKU)"
            :id="FILTER_FIELDS.SKU"
            v-model="filtersValues.sku"
            :label="i18n(sharedTranslations.sku)"
            type="text"
          />
          <RevInputMultiSelect
            v-if="shouldShowFilter(FILTER_FIELDS.MARKET)"
            :id="FILTER_FIELDS.MARKET"
            v-model="marketSelectValue"
            :custom-display-value="setCustomMarketDisplayValue"
            :label="i18n(sharedTranslations.markets)"
            :options="availableMarketsOptions"
          />
          <BackBoxRangeInput
            v-if="shouldShowFilter(FILTER_FIELDS.BACKBOX_PRICE_RANGE)"
            :id="FILTER_FIELDS.BACKBOX_PRICE_RANGE"
            v-model:backboxPriceCurrencyCode="
              filtersValues.backboxPriceCurrencyCode
            "
            v-model:backboxPriceMaxRange="filtersValues.backboxPriceMaxRange"
            v-model:backboxPriceMinRange="filtersValues.backboxPriceMinRange"
            v-model:backboxPriceRangeType="filtersValues.backboxPriceRangeType"
            v-model:withOrWithoutBackbox="filtersValues.hasBackbox"
            :currencyOptions="backMarketRangeCurrencyOptions"
          />
          <RevInputSelect
            v-if="shouldShowFilter(FILTER_FIELDS.PRICING_STRATEGY)"
            :id="FILTER_FIELDS.PRICING_STRATEGY"
            v-model="filtersValues.pricingStrategy"
            :label="i18n(sharedTranslations.pricingStrategy)"
            :options="pricingStrategyOptions"
          />

          <RevInputText
            v-if="shouldShowFilter(FILTER_FIELDS.UUID) && shouldShowAllFilters"
            :id="FILTER_FIELDS.UUID"
            v-model="filtersValues.productId"
            :label="i18n(sharedTranslations.uuid)"
            type="text"
          />

          <RevInputSelect
            v-if="shouldShowFilter(FILTER_FIELDS.GRADE) && shouldShowAllFilters"
            :id="FILTER_FIELDS.GRADE"
            v-model="filtersValues.aestheticGrade"
            :label="i18n(sharedTranslations.grade)"
            :options="gradesOptions"
          />
          <SelectCategory
            :id="FILTER_FIELDS.CATEGORY_ID"
            v-model="filtersValues.categoryId"
            :should-show="
              shouldShowFilter(FILTER_FIELDS.CATEGORY_ID) &&
              shouldShowAllFilters
            "
          />
          <RevInputSelect
            v-if="
              shouldShowFilter(FILTER_FIELDS.BACKBOX) && shouldShowAllFilters
            "
            :id="FILTER_FIELDS.BACKBOX"
            v-model="filtersValues.hasBackbox"
            :label="i18n(sharedTranslations.backbox)"
            :options="backboxOptions"
          />
          <RevInputSelect
            v-if="shouldShowFilter(FILTER_FIELDS.STOCK) && shouldShowAllFilters"
            :id="FILTER_FIELDS.STOCK"
            v-model="filtersValues.hasStock"
            :label="i18n(sharedTranslations.stock)"
            :options="stockOptions"
          />
          <RevInputSelect
            v-if="
              shouldShowFilter(FILTER_FIELDS.HAS_NEW_BATTERY) &&
              shouldShowAllFilters
            "
            :id="FILTER_FIELDS.HAS_NEW_BATTERY"
            v-model="filtersValues.hasNewBattery"
            :label="i18n(sharedTranslations.hasNewBattery)"
            :options="hasNewBatteryOptions"
          />
        </div>
      </div>
      <div class="ml-16">
        <RevButton
          data-qa="submit-listings-filters"
          type="submit"
          variant="primary"
        >
          {{ i18n(sharedTranslations.submit) }}
        </RevButton>
        <RevLink class="ml-16" type="button" @click="resetFilters">
          {{ i18n(sharedTranslations.resetFilters) }}
        </RevLink>
      </div>
    </form>
    <div class="mb-8">
      <RevButton size="small" variant="secondary" @click="toggleMoreFilters">
        <template v-if="shouldShowAllFilters">
          {{ i18n(sharedTranslations.showLessFilters) }}
        </template>
        <template v-else>
          {{ i18n(sharedTranslations.showMoreFilters) }}
        </template>
      </RevButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevInputMultiSelect } from '@ds/components/InputMultiSelect'
import type { MultiSelectOption } from '@ds/components/InputMultiSelect/InputMultiSelect.type'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'

import SelectCategory from '../../../../../../../../components/Input/SelectCategory/SelectCategory.vue'
import { useFiltersOptions } from '../../../../composables/useFiltersOptions'
import { FILTER_FIELDS } from '../../Filters.constants'
import sharedTranslations from '../../Filters.translations'
import type { InitialFilterValues } from '../../Filters.types'
import BackBoxRangeInput from '../BackBoxRangeInput/BackBoxRangeInput.vue'

const i18n = useI18n()

const filtersValues = defineModel<InitialFilterValues>('filtersValues', {
  required: true,
})

const props = defineProps<{
  resetFilters: () => void
  submitFilters: () => void
  shouldShowFilter: (filter: string) => boolean
}>()

const {
  availableMarketsOptions,
  backboxOptions,
  gradesOptions,
  pricingStrategyOptions,
  hasNewBatteryOptions,
  stockOptions,
  backMarketRangeCurrencyOptions,
} = useFiltersOptions()

const shouldShowAllFilters = ref(false)

const tracking = useTracking()

const handleSubmitFilters = () => {
  tracking.trackClick({
    page_type: 'listings',
    zone: 'listings_filters',
    name: 'back_box_price_range_filter_is_applied',
    value: filtersValues.value.backboxPriceRangeType ? 1 : 0,
  })
  props.submitFilters()
}

const marketSelectValue = computed({
  get: () => {
    if (filtersValues.value.markets === '') {
      return ['']
    }

    if (Array.isArray(filtersValues.value.markets)) {
      return filtersValues.value.markets as MarketCountryCode[]
    }

    return [filtersValues.value.markets] as MarketCountryCode[]
  },
  set: (value: Array<MarketCountryCode | ''>) => {
    // If 'All' is selected, we simply set an empty string
    if (value.includes('') && value.length === 0) {
      filtersValues.value.markets = ''
    }
    // If 'All' is selected and there are already other selected markets, we clear the other markets
    if (
      value.includes('') &&
      value.length >= 1 &&
      value.indexOf('') === value.length - 1
    ) {
      filtersValues.value.markets = ''
    }
    // When we set the other markets, we clear the 'All'
    else {
      filtersValues.value.markets = value.filter(
        (item) => item !== '',
      ) as unknown as string
    }
  },
})

const setCustomMarketDisplayValue = (
  selectedOptions: MultiSelectOption[] | undefined,
) => {
  if (!selectedOptions) {
    return undefined
  }
  if (selectedOptions.length <= 1) {
    return undefined
  }

  // If there is more than 1 market selected, we display the number of markets selected
  return i18n(sharedTranslations.marketsSelected, {
    numberOfMarkets: selectedOptions.length,
  })
}

function toggleMoreFilters() {
  shouldShowAllFilters.value = !shouldShowAllFilters.value
}
</script>
